import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';
import ErrorBoundary from './components/ErrorBoundary';

// Eager load Login page (needed immediately)
import { Login } from './pages/Login';

// Lazy load all other pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const CallLogs = lazy(() => import('./pages/CallLogs').then(module => ({ default: module.CallLogs })));
const Reports = lazy(() => import('./pages/Reports'));
const Devices = lazy(() => import('./pages/Devices'));
const Branches = lazy(() => import('./pages/Branches'));
const Users = lazy(() => import('./pages/Users'));
const Admins = lazy(() => import('./pages/Admins'));
const LoginActivities = lazy(() => import('./pages/LoginActivity'));
const EventMonitoring = lazy(() => import('./pages/EventMonitoring'));
const RecordingLifecycle = lazy(() => import('./pages/RecordingLifecycle'));
const Attendance = lazy(() => import('./pages/Attendance'));

// PERFORMANCE FIX: Create a query client with exponential backoff retry logic
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // Smart retry logic: don't retry client errors (4xx), retry server errors (5xx)
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors like 401, 404)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 3 times for 5xx errors (server errors)
        return failureCount < 3;
      },
      // Exponential backoff: 1s, 2s, 4s, max 30s
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Also apply retry logic to mutations (for network failures)
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Only retry once for mutations to avoid duplicate actions
        return failureCount < 1;
      },
      retryDelay: 1000, // 1 second delay for mutation retries
    },
  },
});

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Role-based Protected Route component
const RoleProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles: string[];
}> = ({ children, allowedRoles }) => {
  const { isAuthenticated, admin } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (admin && !allowedRoles.includes(admin.admin_role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Public Route component (redirect to dashboard if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Loading fallback component for lazy-loaded routes
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-neutral-50">
    <div className="text-center">
      <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-rose-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
      <p className="mt-4 text-neutral-600">Loading...</p>
    </div>
  </div>
);

function App() {
  const { initAuth } = useAuthStore();

  // Initialize auth on app load
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  return (
    <QueryClientProvider client={queryClient}>
      {/* PERFORMANCE FIX #20: Error Boundary to catch rendering errors */}
      <ErrorBoundary>
        <Router>
          <div className="App">
            <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* Public Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/call-logs"
                element={
                  <ProtectedRoute>
                    <CallLogs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/devices"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'manager']}>
                    <Devices />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/branches"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <Branches />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute>
                    <Users />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admins"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <Admins />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/login-activities"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'manager']}>
                    <LoginActivities />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/event-monitoring"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'manager']}>
                    <EventMonitoring />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/recording-lifecycle"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <RecordingLifecycle />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/attendance"
                element={
                  <ProtectedRoute>
                    <Attendance />
                  </ProtectedRoute>
                }
              />

              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* 404 - Redirect to dashboard */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
          </div>
        </Router>
      </ErrorBoundary>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#fff',
            color: '#333',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </QueryClientProvider>
  );
}

export default App;
